import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

export default function MyOrders() {
  const { user, ready, authFetch, setAuthModal } = useAuth();
  const [orders, setOrders] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) return;
    authFetch("/my/orders")
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("failed"))))
      .then((d) => setOrders(d.orders || []))
      .catch(() => setErr("Could not load your orders — please refresh."));
  }, [user, authFetch]);

  return (
    <section data-testid="my-orders-page" className="pt-32 pb-24 lg:pt-40">
      <div className="max-w-3xl mx-auto px-6">
        <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[#C85A32] mb-4">Your account</p>
        <h1 className="font-display font-medium tracking-tight text-4xl sm:text-5xl">My orders</h1>

        {!user && ready && (
          <div className="mt-10 rounded-2xl border border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 px-6 py-8" data-testid="my-orders-signin-prompt">
            <p className="text-[#1C130E]/75">Sign in to see your orders in one place.</p>
            <button
              onClick={() => setAuthModal("signin")}
              data-testid="my-orders-signin-btn"
              className="mt-4 rounded-full bg-[#1C130E] text-[#FDFBF7] px-6 py-3 text-sm font-semibold hover:bg-[#C85A32] transition-colors"
            >
              Sign in
            </button>
          </div>
        )}

        {err && <p className="mt-8 text-sm text-red-600">{err}</p>}
        {user && orders && orders.length === 0 && (
          <div className="mt-10 rounded-2xl border border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 px-6 py-8" data-testid="my-orders-empty">
            <p className="text-[#1C130E]/75">No orders yet — your first bake is one tap away.</p>
            <Link
              to="/"
              data-testid="my-orders-menu-link"
              className="mt-4 inline-block rounded-full bg-[#1C130E] text-[#FDFBF7] px-6 py-3 text-sm font-semibold hover:bg-[#C85A32] transition-colors"
            >
              Browse the menu
            </Link>
          </div>
        )}
        {user && orders && orders.length > 0 && (
          <div className="mt-10 space-y-4" data-testid="my-orders-list">
            {orders.map((o) => (
              <div
                key={o.id}
                data-testid="my-order-card"
                className="rounded-2xl border border-[rgba(28,19,14,0.12)] bg-[#FDFBF7]/60 px-5 py-4 flex items-start justify-between gap-4 flex-wrap"
              >
                <div>
                  <p className="font-mono-accent text-[10px] tracking-[0.18em] uppercase text-[#1C130E]/45">
                    #{o.id.slice(0, 8).toUpperCase()} · {new Date(o.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                  <p className="font-display text-lg mt-1">
                    {o.items.map((i) => `${i.qty} × ${i.name}`).join(", ")}
                  </p>
                  <p className="text-sm text-[#1C130E]/60 mt-0.5">
                    {o.fulfillment === "delivery" ? "Home delivery" : "Store pickup"} · ₹{o.total}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-2">
                  <span
                    className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      o.status === "completed"
                        ? "bg-[#1C130E] text-[#FDFBF7]"
                        : o.status === "cancelled"
                          ? "bg-red-50 text-red-700 border border-red-200"
                          : "bg-[#C85A32]/10 text-[#C85A32] border border-[#C85A32]/30"
                    }`}
                    data-testid="my-order-status"
                  >
                    {o.status === "placed" ? "Order received" : o.status === "baking" ? "In the oven" : o.status === "out_for_delivery" ? "Out for delivery" : o.status === "ready" ? "Ready for pickup" : o.status === "completed" ? "Completed" : o.status === "cancelled" ? "Cancelled" : "Confirmed"}
                  </span>
                  <Link
                    to={`/track/${o.id}`}
                    data-testid="my-order-track-link"
                    className="text-xs font-semibold text-[#C85A32] hover:underline"
                  >
                    Track order
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
